import { useEffect, useState } from 'react';

import { getRequest } from '../../../../../services/api';
import { WINNERS_ENDPOINT } from '../constants';
import type { WinnerRow, WinnerTileVM } from '../types';

// The service returns a wrapped result `{ success, message, data: WinnerRow[] }`
// so an empty list still reads as a successful (non-empty) response; after the
// backend's sendResponse wrapper and the axios interceptor the body is
// `{ data: { success, message, data: WinnerRow[] }, errors: [] }`, so the rows
// live at `res.data.data`.
interface WinnersResponse {
  data: {
    success: boolean;
    message: string;
    data: WinnerRow[];
  };
  errors: string[];
}

const toTileVM = (row: WinnerRow): WinnerTileVM => ({
  winId: row.winId,
  nickname: row.nickname,
  gameId: row.gameId,
  gameImageUrl: row.gameImageUrl,
  amount: row.winAmount,
  mode: row.mode,
  isJackpot: row.isJackpot,
  isBingo: row.isBingo,
  isBigWin: row.mode === 'SC' && !row.isJackpot && !row.isBingo && row.isBigWin,
});

interface UseWinnersResult {
  tiles: WinnerTileVM[];
  loading: boolean;
}

export function useWinners(): UseWinnersResult {
  const [tiles, setTiles] = useState<WinnerTileVM[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const res = await getRequest<WinnersResponse>(WINNERS_ENDPOINT, undefined, controller.signal);
        const rows = res?.data?.data ?? [];
        setTiles(rows.map(toTileVM));
      } catch {
        // On failure keep the empty set → the banner hides; recovers on a future load.
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  return { tiles, loading };
}
